import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deviceCount, setDeviceCount] = useState(0);

  const refreshDeviceCount = useCallback(async () => {
    try {
      const { data } = await api.get("/push/status");
      setDeviceCount(data.subscribed_devices || 0);
    } catch {
      /* ignore — unauth or offline */
    }
  }, []);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((s) => setSubscribed(!!s))
    ).catch(() => {});
    refreshDeviceCount();
  }, [refreshDeviceCount]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return { ok: false, reason: "denied" };

      const { data } = await api.get("/push/public-key");
      if (!data.public_key) return { ok: false, reason: "no_vapid" };

      const reg = await navigator.serviceWorker.ready;
      // Always drop any existing subscription first — the previous one may
      // have been created with a different VAPID public key (deploy rotation)
      // and would silently fail with 401/403 on every push.
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch {}
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });

      await api.post("/push/subscribe", {
        subscription: sub.toJSON(),
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      await refreshDeviceCount();
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: e?.message || "error" };
    } finally { setBusy(false); }
  }, [supported, refreshDeviceCount]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { subscription: sub.toJSON() }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      await refreshDeviceCount();
    } finally { setBusy(false); }
  }, [supported, refreshDeviceCount]);

  const refresh = useCallback(async () => {
    // Force a fresh subscription — useful when VAPID keys were rotated or
    // pushes are silently failing (401/403 dead subscription).
    if (!supported) return { ok: false, reason: "unsupported" };
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        await api.post("/push/unsubscribe", { subscription: existing.toJSON() }).catch(() => {});
        try { await existing.unsubscribe(); } catch {}
      }
    } finally { setBusy(false); }
    return await subscribe();
  }, [supported, subscribe]);

  const sendTest = useCallback(async () => {
    return api.post("/push/test", { title: "QRPTS Test", body: "Bildirim çalışıyor 🎉" });
  }, []);

  return {
    supported, permission, subscribed, busy, deviceCount,
    subscribe, unsubscribe, refresh, sendTest, refreshDeviceCount,
  };
}
